/**
 * DeleteConfigButton — Czerwony przycisk usuwania konfiguracji KSeF z confirmem.
 *
 * Confirmation w shadcn AlertDialog (zamiast window.confirm, które jest blokowane
 * w embedded iframe Preview aplikacji Emergent — naprawiono 2026-05-31).
 *
 * Props:
 *   - hasConfig (bool)     — czy w ogóle jest co usuwać (z adminConfig)
 *   - onDeleted (callback) — wywoływane po usunięciu
 */
import { useState } from "react";
import { logger } from "../../../utils/logger";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { deleteAdminConfig } from "../services/ksefApi";
import { safeDetailMessage, safeErrorMessage } from "../utils/errorMessages";

export const DeleteConfigButton = ({ hasConfig = false, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [open, setOpen] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await deleteAdminConfig();
      toast.success("Konfiguracja KSeF usunięta");
      setOpen(false);
      if (onDeleted) onDeleted();
    } catch (err) {
      logger.error("Error deleting config:", err);
      toast.error(safeDetailMessage(err.response) || safeErrorMessage(err) || "Błąd usuwania konfiguracji");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="border border-red-200 rounded-lg p-4 bg-red-50" data-testid="delete-config-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-red-900">Usuń konfigurację KSeF</p>
          <p className="text-xs text-red-700 mt-1">
            Usuwa certyfikat, klucz, hasło i NIP. Operacja nieodwracalna.
          </p>
        </div>

        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger asChild>
            <Button
              disabled={!hasConfig || deleting}
              variant="destructive"
              size="sm"
              data-testid="delete-config-btn"
            >
              {deleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Usuwam...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Usuń konfigurację
                </>
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="delete-config-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Usunąć konfigurację KSeF?</AlertDialogTitle>
              <AlertDialogDescription>
                Operacja jest nieodwracalna. Zostaną usunięte:
                <ul className="list-disc list-inside mt-2 ml-2 text-sm">
                  <li>Certyfikat klienta</li>
                  <li>Klucz prywatny</li>
                  <li>Hasło do klucza</li>
                  <li>Zapisany NIP firmy</li>
                </ul>
                <span className="block mt-3 text-amber-700">
                  Aby ponownie połączyć się z KSeF, trzeba będzie wgrać certyfikat od początku.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-config-cancel-btn" disabled={deleting}>
                Anuluj
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirm}
                disabled={deleting}
                data-testid="delete-config-confirm-btn"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? "Usuwam..." : "Tak, usuń wszystko"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default DeleteConfigButton;
